import { getProject, saveProjectMeta } from "@/services/project-api";
import { deriveTenantId } from "@/store/auth-slice";
import { useStudioStore } from "@/store/studio-store";
import {
  createOpenPencilSourceWorkspace,
  normalizePersistedOpenPencilSourceWorkspace,
  type OpenPencilSourceWorkspace,
} from "./openpencil-source-workspace";
import {
  assertSessionOnlySourcePersistenceAllowed,
  clearSessionOnlyDesignMutationAuthority,
  withCoordinatedOpenPencilWorkspaceWrite,
} from "./openpencil-design-mutation-guard";
import { readOpenPencilSourceWorkspaceToken } from "./openpencil-source-navigation";
import {
  fetchOpenPencilSourceBundleById,
  isOpenPencilSourceRestoreId,
  openPencilSourceRestoreToken,
  type OpenPencilSourceRestoreFailureReason,
} from "./openpencil-source-restore";

export const OPENPENCIL_SOURCE_WORKSPACE_SETTINGS_KEY =
  "openPencilSourceWorkspace";
export const OPENPENCIL_SOURCE_WORKSPACE_REFERENCE_SCHEMA =
  "cogo.studio.openpencil-source-workspace-reference.v1" as const;
const LOCAL_KEY_PREFIX = "cogo.openpencil.sourceWorkspace.";
const SCOPED_LOCAL_KEY_PREFIX = `${LOCAL_KEY_PREFIX}v2.`;

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

interface PersistedOpenPencilSourceWorkspaceReference {
  schemaVersion: typeof OPENPENCIL_SOURCE_WORKSPACE_REFERENCE_SCHEMA;
  restoreToken: string;
  workspace: Omit<OpenPencilSourceWorkspace, "bundle">;
}

type PersistedOpenPencilSourceWorkspace =
  | OpenPencilSourceWorkspace
  | PersistedOpenPencilSourceWorkspaceReference;

/**
 * Server-backed imports already persist their large parsed bundle beside the
 * original source artifact. Persist only the opaque restore handle plus the
 * comparatively small review ledger; embedding the bundle here duplicates
 * megabytes into localStorage and studio_projects.settings.
 */
export function serializeOpenPencilSourceWorkspaceForPersistence(
  workspace: OpenPencilSourceWorkspace | null,
): PersistedOpenPencilSourceWorkspace | null {
  if (!workspace) return null;
  const restoreToken = openPencilSourceRestoreToken(workspace.bundle);
  if (!restoreToken) return structuredClone(workspace);
  const { bundle: _serverBackedBundle, ...workspaceSnapshot } =
    structuredClone(workspace);
  return {
    schemaVersion: OPENPENCIL_SOURCE_WORKSPACE_REFERENCE_SCHEMA,
    restoreToken,
    workspace: workspaceSnapshot,
  };
}

async function hydratePersistedOpenPencilSourceWorkspace(
  value: unknown,
  expectedScope?: {
    projectId?: string | null;
    tenantId?: string | null;
    principalId?: string | null;
  },
): Promise<OpenPencilSourceWorkspace | null> {
  const persisted = record(value);
  if (
    persisted.schemaVersion !== OPENPENCIL_SOURCE_WORKSPACE_REFERENCE_SCHEMA
  ) {
    return normalizePersistedOpenPencilSourceWorkspace(value, expectedScope);
  }
  const restoreToken = persisted.restoreToken;
  const workspaceSnapshot = record(persisted.workspace);
  if (
    !isOpenPencilSourceRestoreId(restoreToken) ||
    typeof workspaceSnapshot.sourceId !== "string"
  ) {
    return null;
  }
  const restored = await fetchOpenPencilSourceBundleById(restoreToken);
  if (
    !restored.ok ||
    restored.bundle.sourceId !== workspaceSnapshot.sourceId ||
    openPencilSourceRestoreToken(restored.bundle) !== restoreToken
  ) {
    return null;
  }
  return normalizePersistedOpenPencilSourceWorkspace(
    { ...workspaceSnapshot, bundle: restored.bundle },
    expectedScope,
  );
}

interface OpenPencilLocalScope {
  principalId: string;
  projectId: string;
}

function currentLocalScope(projectId?: string): OpenPencilLocalScope | null {
  const state = useStudioStore.getState();
  const principalId = state.session?.user?.id?.trim() ?? "";
  const resolvedProjectId =
    projectId?.trim() || state.currentProjectId?.trim() || "workspace_local";
  return principalId
    ? { principalId, projectId: resolvedProjectId }
    : null;
}

function localKey(projectId?: string): string | null {
  const scope = currentLocalScope(projectId);
  return scope
    ? `${SCOPED_LOCAL_KEY_PREFIX}${encodeURIComponent(
        scope.principalId,
      )}.${encodeURIComponent(scope.projectId)}`
    : null;
}

/**
 * Whether the last browser-local write actually stored anything.
 *
 * This is not bookkeeping for its own sake. Measured 2026-07-30 with the real
 * audited source (`[공통] 영업점검색_UI.fig`, 2.7 MB / 4,939 nodes): serializing
 * its workspace throws `QuotaExceededError` on `localStorage`, and the catch
 * below turned that into a `console.warn` nobody sees. So for a real-world
 * design file the product's only persistence silently stored NOTHING, while
 * the interface implied the work was safe. A notice that says "saved in this
 * browser" would repeat that lie, so the badge reads this instead of assuming.
 */
export type OpenPencilLocalPersistenceState = "unknown" | "stored" | "rejected";
export type OpenPencilProjectPersistenceState =
  | "unknown"
  | "stored"
  | "rejected";

let lastLocalPersistence: OpenPencilLocalPersistenceState = "unknown";
let lastProjectPersistence: {
  scope: string;
  state: OpenPencilProjectPersistenceState;
} | null = null;

function projectPersistenceScope(
  workspace = useStudioStore.getState().openPencilSourceWorkspace,
): string | null {
  const state = useStudioStore.getState();
  const principalId = state.session?.user?.id?.trim() ?? "";
  const projectId = state.currentProjectId?.trim() ?? "";
  const sourceId = workspace?.sourceId?.trim() || "__cleared__";
  return state.projectSource === "server" && principalId && projectId
    ? `${principalId}\u0000${projectId}\u0000${sourceId}`
    : null;
}

export function getOpenPencilLocalPersistenceState(): OpenPencilLocalPersistenceState {
  return lastLocalPersistence;
}

/** Test seam — resets the module-scoped observation between cases. */
export function resetOpenPencilLocalPersistenceState(): void {
  lastLocalPersistence = "unknown";
}

export function getOpenPencilProjectPersistenceState(): OpenPencilProjectPersistenceState {
  const scope = projectPersistenceScope();
  return scope && lastProjectPersistence?.scope === scope
    ? lastProjectPersistence.state
    : "unknown";
}

/** Test seam — resets the last authenticated project write observation. */
export function resetOpenPencilProjectPersistenceState(): void {
  lastProjectPersistence = null;
}

function saveLocal(
  workspace: OpenPencilSourceWorkspace | null,
  projectId?: string,
): boolean {
  try {
    const key = localKey(projectId);
    if (!key) {
      if (workspace) lastLocalPersistence = "rejected";
      return false;
    }
    if (workspace) {
      localStorage.setItem(
        key,
        JSON.stringify(
          serializeOpenPencilSourceWorkspaceForPersistence(workspace),
        ),
      );
    }
    else localStorage.removeItem(key);
    if (workspace) lastLocalPersistence = "stored";
    return true;
  } catch (error) {
    // Keep the warn (developer signal) AND record the fact (creator signal).
    console.warn("[openpencil-source] local persistence failed", error);
    lastLocalPersistence = "rejected";
    return false;
  }
}

export function cacheOpenPencilSourceWorkspaceLocally(
  workspace: OpenPencilSourceWorkspace | null,
  projectId?: string,
): boolean {
  return saveLocal(workspace, projectId);
}

async function loadLocal(): Promise<OpenPencilSourceWorkspace | null> {
  try {
    const key = localKey();
    if (!key) return null;
    const raw = localStorage.getItem(key);
    const state = useStudioStore.getState();
    return raw
      ? hydratePersistedOpenPencilSourceWorkspace(JSON.parse(raw), {
          projectId: state.currentProjectId,
          tenantId: deriveTenantId(state.session),
          principalId: state.session?.user?.id,
        })
      : null;
  } catch {
    return null;
  }
}

export async function readOpenPencilSourceWorkspaceFromSettings(
  settings: unknown,
  expectedScope?: {
    projectId?: string | null;
    tenantId?: string | null;
    principalId?: string | null;
  },
): Promise<OpenPencilSourceWorkspace | null> {
  return hydratePersistedOpenPencilSourceWorkspace(
    record(settings)[OPENPENCIL_SOURCE_WORKSPACE_SETTINGS_KEY],
    expectedScope,
  );
}

export async function saveOpenPencilSourceWorkspaceToCurrentProject(
  workspace = useStudioStore.getState().openPencilSourceWorkspace,
): Promise<boolean> {
  const state = useStudioStore.getState();
  if (state.projectSource !== "server" && state.currentProjectId) {
    assertSessionOnlySourcePersistenceAllowed(state.currentProjectId);
  }
  saveLocal(workspace);
  if (state.projectSource !== "server" || !state.currentProjectId) return false;
  const persistenceScope = projectPersistenceScope(workspace);
  if (!persistenceScope) return false;
  lastProjectPersistence = { scope: persistenceScope, state: "unknown" };
  try {
    const project = await getProject(state.currentProjectId);
    if (!project) {
      throw new Error("OPENPENCIL_SOURCE_PROJECT_READ_FAILED");
    }
    await saveProjectMeta(state.currentProjectId, {
      settings: {
        ...record(project?.settings),
        [OPENPENCIL_SOURCE_WORKSPACE_SETTINGS_KEY]:
          serializeOpenPencilSourceWorkspaceForPersistence(workspace),
      },
    });
    lastProjectPersistence = { scope: persistenceScope, state: "stored" };
    return true;
  } catch (error) {
    lastProjectPersistence = { scope: persistenceScope, state: "rejected" };
    throw error;
  }
}

export async function loadOpenPencilSourceWorkspaceFromCurrentProject(): Promise<OpenPencilSourceWorkspace | null> {
  const state = useStudioStore.getState();
  let workspace: OpenPencilSourceWorkspace | null = null;
  if (state.projectSource === "server" && state.currentProjectId) {
    const project = await getProject(state.currentProjectId);
    workspace = await readOpenPencilSourceWorkspaceFromSettings(
      project?.settings,
      {
        projectId: state.currentProjectId,
        tenantId: deriveTenantId(state.session),
        principalId: state.session?.user?.id,
      },
    );
  }
  workspace ??= await loadLocal();
  // Nothing in this browser. If the URL names an import, recover it from the
  // server — this is the leg that makes an imported source survive a different
  // device, a private window, or cleared storage. See the module header of
  // `openpencil-source-restore.ts`: the source bytes, previews and bundle have
  // always been server-side, so this is a read of what already exists, not a
  // new copy of anyone's design data.
  if (!workspace) {
    const restored = await restoreOpenPencilSourceWorkspaceFromUrl();
    workspace = restored.workspace;
  }
  if (workspace) {
    if (state.projectSource !== "server" && state.currentProjectId) {
      clearSessionOnlyDesignMutationAuthority(state.currentProjectId);
    }
    withCoordinatedOpenPencilWorkspaceWrite(() =>
      useStudioStore.getState().setOpenPencilSourceWorkspace(workspace),
    );
  }
  return workspace;
}

export interface OpenPencilSourceRestoreOutcome {
  workspace: OpenPencilSourceWorkspace | null;
  /** `null` when there was no token to act on — i.e. nothing was attempted. */
  attemptedToken: string | null;
  failure: OpenPencilSourceRestoreFailureReason | null;
}

/**
 * Rebuild the source workspace named by the current URL from the server.
 *
 * Only the IMPORT is recovered (document, pages, surface candidates, tokens,
 * preview manifest) — review decisions are written to this browser alone and
 * are not part of the restore. `createOpenPencilSourceWorkspace` therefore
 * yields a workspace with pending decisions rather than fabricating verdicts
 * nobody recorded.
 *
 * The result is cached back into localStorage so the ordinary local path
 * serves subsequent loads in this browser; it is deliberately NOT written to
 * the server, because `saveOpenPencilSourceWorkspaceToCurrentProject` is
 * fenced by `assertSessionOnlySourcePersistenceAllowed` for non-server
 * projects and restoring must not become a way around that fence.
 */
export async function restoreOpenPencilSourceWorkspaceFromUrl(): Promise<OpenPencilSourceRestoreOutcome> {
  if (typeof window === "undefined") {
    return { workspace: null, attemptedToken: null, failure: null };
  }
  const token = readOpenPencilSourceWorkspaceToken(window.location.pathname);
  if (!token) return { workspace: null, attemptedToken: null, failure: null };

  const result = await fetchOpenPencilSourceBundleById(token);
  if (!result.ok) {
    return { workspace: null, attemptedToken: token, failure: result.reason };
  }
  const workspace = createOpenPencilSourceWorkspace(result.bundle);
  cacheOpenPencilSourceWorkspaceLocally(workspace);
  return { workspace, attemptedToken: token, failure: null };
}

export async function ensureOpenPencilSourceWorkspaceLoaded(): Promise<OpenPencilSourceWorkspace | null> {
  const current = useStudioStore.getState().openPencilSourceWorkspace;
  return current ?? loadOpenPencilSourceWorkspaceFromCurrentProject();
}

export async function clearOpenPencilSourceWorkspaceFromCurrentProject(): Promise<void> {
  if (useStudioStore.getState().openPencilSourceWorkspace?.candidateBinding) {
    throw new Error(
      "The active source-derived candidate must retain its source authority. Open another project before clearing the source workspace.",
    );
  }
  useStudioStore.getState().clearOpenPencilFigmaSource();
  await saveOpenPencilSourceWorkspaceToCurrentProject(null);
}

/**
 * Privacy boundary for logout. Legacy unscoped entries are removed as well;
 * they are never adopted into the next authenticated principal's project.
 */
export function clearAllOpenPencilSourceWorkspaceCachesForLogout(): void {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(LOCAL_KEY_PREFIX)) localStorage.removeItem(key);
    }
  } catch (error) {
    console.warn("[openpencil-source] logout cleanup failed", error);
  } finally {
    useStudioStore.getState().clearOpenPencilFigmaSource();
    lastLocalPersistence = "unknown";
    lastProjectPersistence = null;
  }
}
